import { eachDayOfInterval, format, getISODay, parseISO } from 'date-fns';
import {
  MaximumFeeDetail,
  ProgressiveTier,
  RateDetail,
  RegularRule,
  SpecificRule,
} from '../models/ParkingRate.ts';
import { DayOfWeek } from '../models/OperationHours.ts';

// ─── Constants ────────────────────────────────────────────────────────────────

export const ALL_DOWS: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export const DOW_SHORT: Record<DayOfWeek, string> = {
  MONDAY: '一',
  TUESDAY: '二',
  WEDNESDAY: '三',
  THURSDAY: '四',
  FRIDAY: '五',
  SATURDAY: '六',
  SUNDAY: '日',
};

/** JS getDay() 0=Sun → 轉成中文字（週日~週六） */
export const DOW_ZH = ['日', '一', '二', '三', '四', '五', '六'];

// ─── Types ────────────────────────────────────────────────────────────────────

/** 單一費率時段 */
export interface DaySlot {
  startTime: string;
  endTime: string;
  rate: RateDetail | null;
}

/** 單一最高收費時段 */
export interface MaxFeeSlot {
  startTime: string;
  endTime: string;
  maximumFee: MaximumFeeDetail[];
}

// ─── 費率結構 → 語意解析 ────────────────────────────────────────────────────────

export type TierPosition = 'only' | 'first' | 'middle' | 'last';

/** 累進費率單段的解析結果 */
export interface ParsedTier {
  position: TierPosition;
  /** "前 60 分鐘" | "第 60 ~ 120 分鐘" | "第 120 分鐘後";only 段為 null */
  timeLabel: string | null;
  isFree: boolean;
  intervalMinutes: number;
  fee: number;
}

/**
 * 任一費率結構的解析結果。
 * 各元件只需 switch `kind`,再套用自己的樣式即可,不必重複判斷結構。
 */
export type ParsedRate =
  | { kind: 'free' }
  | { kind: 'per_entry'; fee: number }
  | { kind: 'progressive'; tiers: ParsedTier[] };

/**
 * 兩種「免費」表示法:
 * 1. PER_ENTRY 且 fee === 0
 * 2. PROGRESSIVE 且無 tier,或單一 tier 且 fee === 0(interval 任意)
 */
function isFreeRate(rate: RateDetail): boolean {
  if (rate.type === 'PER_ENTRY') return rate.fee === 0;
  return (
    rate.tiers.length === 0 ||
    (rate.tiers.length === 1 && rate.tiers[0].fee === 0)
  );
}

/** 依 index 與總數判斷該 tier 在累進費率中的位置 */
function getTierPosition(index: number, total: number): TierPosition {
  if (total === 1) return 'only';
  if (index === 0) return 'first';
  if (index === total - 1) return 'last';
  return 'middle';
}

/**
 * 累進費率某段的時間標籤:
 * - first  → "前 60 分鐘"
 * - middle → "第 60 ~ 120 分鐘"
 * - last   → "第 120 分鐘後"
 * - only   → null（單段不需標時間範圍）
 */
function formatTierTimeLabel(
  tier: ProgressiveTier,
  position: TierPosition,
): string | null {
  switch (position) {
    case 'first':
      return `前 ${tier.endMinute} 分鐘`;
    case 'middle':
      return `第 ${tier.startMinute} ~ ${tier.endMinute} 分鐘`;
    case 'last':
      return `第 ${tier.startMinute} 分鐘後`;
    case 'only':
      return null;
  }
}

/**
 * 費率結構的單一解析入口:RateDetail → ParsedRate。
 * 「免費」判斷、tier 位置、時間標籤等邏輯集中於此,呈現層只讀結果。
 */
export function parseRate(rate: RateDetail): ParsedRate {
  if (isFreeRate(rate)) return { kind: 'free' };
  if (rate.type === 'PER_ENTRY') return { kind: 'per_entry', fee: rate.fee };
  return {
    kind: 'progressive',
    tiers: rate.tiers.map((tier, i) => {
      const position = getTierPosition(i, rate.tiers.length);
      return {
        position,
        timeLabel: formatTierTimeLabel(tier, position),
        isFree: tier.fee === 0,
        intervalMinutes: tier.intervalMinutes,
        fee: tier.fee,
      };
    }),
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** 取得 [startStr, endStr] 範圍內所有日期（YYYY-MM-DD） */
export function getDatesInRange(start: string, end: string): string[] {
  return eachDayOfInterval({
    start: parseISO(start),
    end: parseISO(end),
  }).map((d) => format(d, 'yyyy-MM-dd'));
}

/** "YYYY-MM-DD" → "M/D" */
export const dateToShortLabel = (s: string): string =>
  format(parseISO(s), 'M/d');

/**
 * 給定某天已有的 DaySlot[]（已依 startTime 排序），
 * 找出 00:00–23:59 內的空缺並插入 rate:null 的 gap slot。
 * 整天無資料時，回傳單一 gap slot [00:00–23:59]。
 */
export function fillDayGaps(slots: DaySlot[]): DaySlot[] {
  const DAY_START = '00:00';
  const DAY_END = '23:59';
  if (slots.length === 0) {
    return [{ startTime: DAY_START, endTime: DAY_END, rate: null }];
  }
  const result: DaySlot[] = [];
  let cursor = DAY_START;
  for (const slot of slots) {
    if (slot.startTime > cursor) {
      result.push({ startTime: cursor, endTime: slot.startTime, rate: null });
    }
    result.push(slot);
    cursor = slot.endTime;
  }
  if (cursor < DAY_END) {
    result.push({ startTime: cursor, endTime: DAY_END, rate: null });
  }
  return result;
}

const rateKey = (r: RateDetail | null): string =>
  r === null ? '' : JSON.stringify(r);

/**
 * RegularRule 的所有 ratePeriods → 依「星期幾」分組成卡牌。
 * effectivePeriod 範圍內但無資料的 DOW，以及當天有空缺的時段，
 * 皆會以 rate:null (未知) 補齊。
 * 相同完整時段表的幾天會合併成同一張卡。
 * 每張卡包含 maxFeeSlots（該 DOW 對應的最高收費時段）。
 */
export function buildRegularDayCards(
  rule: RegularRule,
): { dows: DayOfWeek[]; slots: DaySlot[]; maxFeeSlots: MaxFeeSlot[] }[] {
  const startStr = rule.effectivePeriod.startDateTime.split('T')[0];
  const endStr = rule.effectivePeriod.endDateTime.split('T')[0];
  const scopedDates = getDatesInRange(startStr, endStr);
  const scopedDowSet = new Set<DayOfWeek>();
  scopedDates.forEach((d) => {
    // getISODay: 1=Mon … 7=Sun，直接對應 ALL_DOWS 索引（減 1）
    scopedDowSet.add(ALL_DOWS[getISODay(parseISO(d)) - 1]);
  });

  const dowSlotsMap = new Map<DayOfWeek, DaySlot[]>();
  rule.ratePeriods.forEach((period) => {
    period.dayOfWeekTimeSlots.forEach((slot) => {
      const dow = slot.startDayOfWeek;
      if (!dowSlotsMap.has(dow)) dowSlotsMap.set(dow, []);
      dowSlotsMap.get(dow)!.push({
        startTime: slot.startTime.slice(0, 5),
        endTime: slot.endTime.slice(0, 5),
        rate: period.rate,
      });
    });
  });
  dowSlotsMap.forEach((slots) =>
    slots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  );

  const dowMaxFeeMap = new Map<DayOfWeek, MaxFeeSlot[]>();
  rule.maximumFeeRules.forEach((period) => {
    period.dayOfWeekTimeSlots.forEach((slot) => {
      const dow = slot.startDayOfWeek;
      if (!dowMaxFeeMap.has(dow)) dowMaxFeeMap.set(dow, []);
      dowMaxFeeMap.get(dow)!.push({
        startTime: slot.startTime.slice(0, 5),
        endTime: slot.endTime.slice(0, 5),
        maximumFee: period.maximumFee,
      });
    });
  });
  dowMaxFeeMap.forEach((slots) =>
    slots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  );

  const fpMap = new Map<string, DayOfWeek[]>();
  ALL_DOWS.forEach((dow) => {
    if (!scopedDowSet.has(dow)) return;
    const filled = fillDayGaps(dowSlotsMap.get(dow) ?? []);
    const rateFp = filled
      .map((s) => `${s.startTime}|${s.endTime}|${rateKey(s.rate)}`)
      .join(';');
    const maxFeeList = dowMaxFeeMap.get(dow) ?? [];
    const maxFeeFp = maxFeeList
      .map((s) => `${s.startTime}|${s.endTime}|${JSON.stringify(s.maximumFee)}`)
      .join(';');
    const fp = `${rateFp}||${maxFeeFp}`;
    if (!fpMap.has(fp)) fpMap.set(fp, []);
    fpMap.get(fp)!.push(dow);
  });

  return [...fpMap.entries()]
    .map(([, dows]) => ({
      dows,
      slots: fillDayGaps(dowSlotsMap.get(dows[0]) ?? []),
      maxFeeSlots: dowMaxFeeMap.get(dows[0]) ?? [],
    }))
    .sort((a, b) => ALL_DOWS.indexOf(a.dows[0]) - ALL_DOWS.indexOf(b.dows[0]));
}

/**
 * SpecificRule 的所有 ratePeriods → 依「日期」分組成卡牌。
 * effectivePeriod 範圍內但無資料的日期，以及當天有空缺的時段，
 * 皆會以 rate:null (未知) 補齊。
 * 相同完整時段表的幾天會合併成同一張卡。
 * 每張卡包含 maxFeeSlots（該日期對應的最高收費時段）。
 */
export function buildSpecificDayCards(
  rule: SpecificRule,
): { dates: string[]; slots: DaySlot[]; maxFeeSlots: MaxFeeSlot[] }[] {
  const startStr = rule.effectivePeriod.startDateTime.split('T')[0];
  const endStr = rule.effectivePeriod.endDateTime.split('T')[0];
  const allDates = getDatesInRange(startStr, endStr);

  const dateSlotsMap = new Map<string, DaySlot[]>();
  rule.ratePeriods.forEach((period) => {
    period.dateTimeSlots.forEach((slot) => {
      const date = slot.startDateTime.split('T')[0];
      if (!dateSlotsMap.has(date)) dateSlotsMap.set(date, []);
      dateSlotsMap.get(date)!.push({
        startTime: format(parseISO(slot.startDateTime), 'HH:mm'),
        endTime: format(parseISO(slot.endDateTime), 'HH:mm'),
        rate: period.rate,
      });
    });
  });
  dateSlotsMap.forEach((slots) =>
    slots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  );

  const dateMaxFeeMap = new Map<string, MaxFeeSlot[]>();
  rule.maximumFeeRules.forEach((period) => {
    period.dateTimeSlots.forEach((slot) => {
      const date = slot.startDateTime.split('T')[0];
      if (!dateMaxFeeMap.has(date)) dateMaxFeeMap.set(date, []);
      dateMaxFeeMap.get(date)!.push({
        startTime: format(parseISO(slot.startDateTime), 'HH:mm'),
        endTime: format(parseISO(slot.endDateTime), 'HH:mm'),
        maximumFee: period.maximumFee,
      });
    });
  });
  dateMaxFeeMap.forEach((slots) =>
    slots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  );

  const fpMap = new Map<string, string[]>();
  allDates.forEach((date) => {
    const filled = fillDayGaps(dateSlotsMap.get(date) ?? []);
    const rateFp = filled
      .map((s) => `${s.startTime}|${s.endTime}|${rateKey(s.rate)}`)
      .join(';');
    const maxFeeList = dateMaxFeeMap.get(date) ?? [];
    const maxFeeFp = maxFeeList
      .map((s) => `${s.startTime}|${s.endTime}|${JSON.stringify(s.maximumFee)}`)
      .join(';');
    const fp = `${rateFp}||${maxFeeFp}`;
    if (!fpMap.has(fp)) fpMap.set(fp, []);
    fpMap.get(fp)!.push(date);
  });

  return [...fpMap.entries()]
    .map(([, dates]) => ({
      dates,
      slots: fillDayGaps(dateSlotsMap.get(dates[0]) ?? []),
      maxFeeSlots: dateMaxFeeMap.get(dates[0]) ?? [],
    }))
    .sort((a, b) => a.dates[0].localeCompare(b.dates[0]));
}
